import {Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialog, MatDialogRef} from '@angular/material/dialog';
import {Shift} from '@app/core/models/shift.model';
import {User} from '@app/core/models/user.model';
import {IQOSelectData} from '@quickorder/angular-web-lib/lib/types/interfaces/q-o-select-data';
import {Group} from '@app/core/models/group.model';
import {Subject, timer} from 'rxjs';
import {Store} from '@ngrx/store';
import {IAppState} from '@app/core/store/state/app.state';
import {PunchInUser, PunchOutUser} from '@app/core/store/actions/punch-clock.actions';
import {SnackBar} from '@app/core/services/snackbar.service';
import {PunchUserPinComponent} from '@app/support/modals/punch-user-pin/punch-user-pin.component';
import {PunchUserNewPinComponent} from '@app/support/modals/punch-user-new-pin/punch-user-new-pin.component';
import {getCurrentState} from '@app/core/helpers';
import {take, takeUntil} from 'rxjs/operators';
import * as moment from 'moment';
import {Moment} from 'moment';
import {EQORebrandColor, EQORebrandIcon} from '@quickorder/angular-web-lib';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';

@Component({
	selector: 'app-punch-user-popup',
	templateUrl: './punch-user-popup.component.html',
	styleUrls: ['./punch-user-popup.component.scss'],
})
export class PunchUserPopupComponent implements OnInit, OnDestroy {
	public EQORebrandIcon: typeof EQORebrandIcon = EQORebrandIcon;
	public EQORebrandColor: typeof EQORebrandColor = EQORebrandColor;

	public groupsSelectData: IQOSelectData<Group>[] = [];
	public selectedGroup: IQOSelectData<Group>;

	public dateNow: Moment = moment();
	public loadingLocation: boolean;
	public location: any;

	public punchNote: string;
	public form: FormGroup;
	private _destroy$: Subject<void> = new Subject<void>();

	constructor(
		public dialogRef: MatDialogRef<PunchUserPopupComponent>,
		@Inject(MAT_DIALOG_DATA) public data: { punchedIn: boolean, shift: Shift, user: User },
		private store: Store<IAppState>,
		private snackBar: SnackBar,
		private dialog: MatDialog,
		private formBuilder: FormBuilder,
	) {
	}

	public get selectedGroupControl(): FormControl {
		return this.form.get('selectedGroup') as FormControl;
	}

	public ngOnInit(): void {
		this.initForm();
		this.initSubscriptions();
		this.groupsSelectData = this.data.user.groups.map(gr => ({
			data: gr,
			text: gr.group_name,
		}));
		if (this.data.user.shift) {
			this.selectedGroupControl.patchValue(
				{
					data: this.data.user.shift.group,
					text: this.data.user.shift.group.group_name,
				},
			);
		}
	}

	public close(): void {
		this.dialogRef.close();
	}

	public punch(pinCode?: number): void {
		if (this.data.punchedIn) {
			this.store.dispatch(new PunchOutUser(this.data.user.id, this.form.value.punchNote, pinCode));
		} else {
			this.store.dispatch(new PunchInUser(this.data.user.id, Number(this.selectedGroupControl.value?.data.id), pinCode));
		}
	}

	public punchButtonPressed(): void {
		if (!getCurrentState(this.store).punchClock.department.punch_user_pin_enabled) {
			this.punch();

			return;
		}

		let dialogRef;
		if (this.data.user.pin_code_is_set) {
			dialogRef = this.dialog.open(PunchUserPinComponent, {
				data: {
					user: this.data.user,
					punchedIn: this.data.punchedIn,
				},
			});
		} else {
			dialogRef = this.dialog.open(PunchUserNewPinComponent, {
				data: {
					user: this.data.user,
					punchedIn: this.data.punchedIn,
				},
				id: 'punchNewPinModal',
			});
		}

		dialogRef.afterClosed().pipe(take(1)).subscribe(res => {
			if (res && res.success) {
				this.punch(res.pinCode);
			}
		});
	}

	public ngOnDestroy(): void {
		this._destroy$.next();
		this._destroy$.complete();
	}

	private initForm(): void {
		this.form = this.formBuilder.group({
			punchNote: [null],
			selectedGroup: [null, Validators.required],
		});
	}

	private initSubscriptions(): void {
		timer(0, 1000)
			.pipe(takeUntil(this._destroy$))
			.subscribe(() => {
				this.dateNow = moment();
			});
	}
}
